import React, { useState, useEffect, useRef } from 'react';
import { useYoco } from '../contexts/YocoContext';
import { useAuth } from '../contexts/AuthContext';
import { Plan } from './PlanSelection';

interface PaymentFormProps {
  selectedPlan: Plan;
  onPaymentSuccess: (subscriptionId: string) => void;
  onPaymentError: (error: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  selectedPlan,
  onPaymentSuccess,
  onPaymentError,
  loading,
  setLoading
}) => {
  const { yoco, isLoaded } = useYoco();
  const { getIdToken } = useAuth();
  const [cardError, setCardError] = useState<string>('');
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const cardRef = useRef<HTMLDivElement>(null);

  const validateCardDetails = () => {
    const errors = [];
    
    if (!cardDetails.number || cardDetails.number.replace(/\s/g, '').length < 13) {
      errors.push('Please enter a valid card number');
    }
    
    if (!cardDetails.expiryMonth || parseInt(cardDetails.expiryMonth) < 1 || parseInt(cardDetails.expiryMonth) > 12) {
      errors.push('Please enter a valid expiry month (01-12)');
    }
    
    if (!cardDetails.expiryYear || parseInt(cardDetails.expiryYear) < 23) {
      errors.push('Please enter a valid expiry year');
    }
    
    if (!cardDetails.cvv || cardDetails.cvv.length < 3) {
      errors.push('Please enter a valid CVV');
    }
    
    return errors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!yoco || !isLoaded) {
      setCardError('Payment system not loaded. Please try again.');
      return;
    }

    // Validate card details
    const validationErrors = validateCardDetails();
    if (validationErrors.length > 0) {
      setCardError(validationErrors[0]);
      return;
    }

    setLoading(true);
    setCardError('');

    try {
      // Create payment token with Yoco
      const tokenRequest = {
        number: cardDetails.number.replace(/\s/g, ''),
        expiryMonth: cardDetails.expiryMonth.padStart(2, '0'),
        expiryYear: cardDetails.expiryYear.length === 2 ? `20${cardDetails.expiryYear}` : cardDetails.expiryYear,
        cvv: cardDetails.cvv,
      };

      const { error, token } = await yoco.createToken(tokenRequest);

      if (error) {
        setCardError(error.message || 'Invalid card details. Please check and try again.');
        setLoading(false);
        return;
      }

      if (!token || !token.id) {
        setCardError('Failed to process payment. Please try again.');
        setLoading(false);
        return;
      }

      // Call backend to create subscription
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`,
        },
        body: JSON.stringify({
          plan: selectedPlan.id,
          payment_token: token.id,
          amount: Math.round(selectedPlan.price * 100), // Convert to cents
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Subscription creation failed');
      }

      onPaymentSuccess(result.subscription_id);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Payment failed';
      setCardError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 16) {
      setCardDetails({ ...cardDetails, number: formatted });
    }
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 2) {
      setCardDetails({ ...cardDetails, expiryMonth: value });
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 2) {
      setCardDetails({ ...cardDetails, expiryYear: value });
    }
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      setCardDetails({ ...cardDetails, cvv: value });
    }
  };



  if (selectedPlan.price === 0) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Free Plan Selected</h3>
        <p className="text-sm text-gray-600">
          No payment required. You can upgrade to a paid plan at any time.
        </p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <svg className="animate-spin w-8 h-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Payment System</h3>
        <p className="text-sm text-gray-600">
          Please wait while we load the secure payment system...
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h3>
        
        <div className="bg-gray-50 rounded-macos p-4 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-900">{selectedPlan.name} Plan</span>
            <span className="text-lg font-bold text-gray-900">
              R{selectedPlan.price}/{selectedPlan.period.replace('per ', '')}
            </span>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Secure payment powered by Yoco
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="card-number" className="block text-sm font-medium text-gray-700 mb-2">
              Card Number
            </label>
            <input
              id="card-number"
              type="text"
              placeholder="1234 5678 9012 3456"
              value={cardDetails.number}
              onChange={handleCardNumberChange}
              className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              style={{ backgroundColor: 'white', color: '#111827' }}
              required
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="expiry-month" className="block text-sm font-medium text-gray-700 mb-2">
                Month
              </label>
              <input
                id="expiry-month"
                type="text"
                placeholder="MM"
                value={cardDetails.expiryMonth}
                onChange={handleExpiryChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                style={{ backgroundColor: 'white', color: '#111827' }}
                required
              />
            </div>
            
            <div>
              <label htmlFor="expiry-year" className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <input
                id="expiry-year"
                type="text"
                placeholder="YY"
                value={cardDetails.expiryYear}
                onChange={handleYearChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                style={{ backgroundColor: 'white', color: '#111827' }}
                required
              />
            </div>
            
            <div>
              <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-2">
                CVV
              </label>
              <input
                id="cvv"
                type="text"
                placeholder="123"
                value={cardDetails.cvv}
                onChange={handleCvvChange}
                className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                style={{ backgroundColor: 'white', color: '#111827' }}
                required
              />
            </div>
          </div>
        </div>

        {cardError && (
          <div className="mt-2 text-sm text-red-600">
            {cardError}
          </div>
        )}
      </div>

      <div className="text-xs text-gray-500">
        <p>
          By subscribing, you agree to our terms of service and privacy policy. 
          Your subscription will automatically renew unless cancelled.
        </p>
      </div>

      <button
        type="submit"
        disabled={!isLoaded || loading}
        className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-macos text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
      >
        {loading ? (
          <div className="flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </div>
        ) : (
          `Subscribe to ${selectedPlan.name} - R${selectedPlan.price}/${selectedPlan.period.replace('per ', '')}`
        )}
      </button>
    </form>
  );
};

export default PaymentForm;